import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';

import { AssetPickerBrowseButton } from './asset-picker-control';
import { fieldControlCss } from './field-control-style';
import { segmentedControlCss, SEGMENTED_SELECTED_COLOR } from './segmented-control-style';

type ImageSourceTab = 'library' | 'url';

/** Accept explicit image locations, not bare strings the carousel would silently replace. */
export function isValidImageUrl(value: string): boolean {
  if (value === '') return true;
  if (/[\r\n\t\\]/.test(value)) return false;
  if (/^(?:\/(?!\/)|\.\.?\/)/.test(value)) return true;
  if (/^data:image\/[a-z0-9.+-]+(?:;[^,]*)?,.+$/i.test(value)) return true;
  if (!/^https?:\/\/[^/]/i.test(value)) return false;
  try {
    return new URL(value).hostname !== '';
  } catch {
    return false;
  }
}

const SOURCE_TABS: ReadonlyArray<{ id: ImageSourceTab; label: string }> = [
  { id: 'library', label: 'Library' },
  { id: 'url', label: 'URL' },
];

const Stack = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const TabList = styled.div`
  ${segmentedControlCss}
  --_cms-segmented-bg: var(--dcm-cms-image-source-action-bg, var(--dcm-cms-segmented-bg, var(--puck-field-color-bg, #fff)));
  --_cms-segmented-color: var(
    --dcm-cms-image-source-action-color,
    var(--dcm-cms-segmented-color, var(--puck-color-text, #121516))
  );
  --_cms-segmented-border: var(
    --dcm-cms-image-source-action-border,
    var(--dcm-cms-segmented-border-color, var(--puck-color-border, #d0d4d8))
  );
  --_cms-segmented-radius: var(
    --dcm-cms-image-source-action-radius,
    var(--dcm-cms-segmented-radius, var(--puck-radius-m, 6px))
  );
  --_cms-segmented-selected-color: var(--dcm-cms-image-source-selected, ${SEGMENTED_SELECTED_COLOR});
  --_cms-segmented-focus: var(
    --dcm-cms-image-source-focus,
    var(--dcm-cms-segmented-focus, var(--puck-color-focus-ring, #0b99e6))
  );
`;

const TabButton = styled.button``;

const UrlInput = styled.input`
  ${fieldControlCss}
  border-color: var(
    --dcm-cms-image-source-action-border,
    var(--puck-field-color-border, var(--puck-color-border, #d0d4d8))
  );
  border-radius: var(--dcm-cms-image-source-action-radius, var(--puck-field-radius, var(--puck-radius-m, 6px)));

  &:focus-visible {
    outline: 2px solid var(--dcm-cms-image-source-focus, var(--puck-color-azure-05, #0b99e6));
    outline-offset: 2px;
  }
`;

const UrlError = styled.span`
  color: var(--dcm-cms-image-source-error-color, var(--puck-color-red-04, #a52424));
  display: block;
  font-size: 12px;
  margin-top: 4px;
`;

type LibraryUrlTabsProps = {
  enterpriseId: string | undefined;
  fieldLabel: string;
  name: string;
  value: string;
  onBrowseLibrary: () => void;
  onUrlChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  browseTestId: string;
  inputTestId: string;
};

/** Shared Library-first image chooser for string-URL fields. */
export function LibraryUrlTabs({
  enterpriseId,
  fieldLabel,
  name,
  value,
  onBrowseLibrary,
  onUrlChange,
  readOnly,
  browseTestId,
  inputTestId,
}: LibraryUrlTabsProps): JSX.Element {
  const [activeTab, setActiveTab] = useState<ImageSourceTab>(() => (value ? 'url' : 'library'));
  const [urlDraft, setUrlDraft] = useState(value);
  useEffect(() => setUrlDraft(value), [value]);
  const hasUrlError = !isValidImageUrl(urlDraft);
  const handleUrlChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (readOnly) return;
      const nextValue = event.currentTarget.value;
      setUrlDraft(nextValue);
      if (isValidImageUrl(nextValue)) onUrlChange(event);
    },
    [onUrlChange, readOnly]
  );
  const reactId = useId();
  const idBase = `library-url-${name.replace(/[^a-zA-Z0-9_-]/g, '-')}-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const libraryTabRef = useRef<HTMLButtonElement>(null);
  const urlTabRef = useRef<HTMLButtonElement>(null);

  const tabRefFor = useCallback(
    (tab: ImageSourceTab): React.RefObject<HTMLButtonElement> => (tab === 'url' ? urlTabRef : libraryTabRef),
    []
  );

  const activateTab = useCallback(
    (tab: ImageSourceTab, moveFocus = false) => {
      setActiveTab(tab);
      if (moveFocus) requestAnimationFrame(() => tabRefFor(tab).current?.focus());
    },
    [tabRefFor]
  );

  const handleTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, tab: ImageSourceTab) => {
      const currentIndex = SOURCE_TABS.findIndex(({ id }) => id === tab);
      let nextIndex: number | undefined;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % SOURCE_TABS.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + SOURCE_TABS.length) % SOURCE_TABS.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = SOURCE_TABS.length - 1;
      if (nextIndex === undefined) return;
      event.preventDefault();
      activateTab(SOURCE_TABS[nextIndex].id, true);
    },
    [activateTab]
  );

  const tabIdFor = (tab: ImageSourceTab): string => `${idBase}-${tab}-tab`;
  const panelIdFor = (tab: ImageSourceTab): string => `${idBase}-${tab}-panel`;

  return (
    <Stack>
      <TabList role='tablist' aria-label={`${fieldLabel} source`}>
        {SOURCE_TABS.map(({ id, label }) => (
          <TabButton
            key={id}
            ref={tabRefFor(id)}
            id={tabIdFor(id)}
            type='button'
            role='tab'
            aria-controls={panelIdFor(id)}
            aria-label={label}
            aria-selected={activeTab === id}
            tabIndex={activeTab === id ? 0 : -1}
            disabled={readOnly}
            onClick={() => activateTab(id)}
            onKeyDown={(event) => handleTabKeyDown(event, id)}
          >
            {label}
          </TabButton>
        ))}
      </TabList>

      <div
        id={panelIdFor('library')}
        role='tabpanel'
        aria-labelledby={tabIdFor('library')}
        hidden={activeTab !== 'library'}
      >
        <AssetPickerBrowseButton
          enterpriseId={enterpriseId}
          onClick={onBrowseLibrary}
          disabled={readOnly}
          testId={browseTestId}
          style={{ width: '100%' }}
        />
      </div>

      <div id={panelIdFor('url')} role='tabpanel' aria-labelledby={tabIdFor('url')} hidden={activeTab !== 'url'}>
        <UrlInput
          type='text'
          value={urlDraft}
          onChange={handleUrlChange}
          disabled={readOnly}
          placeholder='https://...'
          aria-label={`${fieldLabel} URL`}
          aria-invalid={hasUrlError}
          aria-describedby={hasUrlError ? `${idBase}-url-error` : undefined}
          data-testid={inputTestId}
        />
        {hasUrlError && (
          <UrlError id={`${idBase}-url-error`} role='alert'>
            Enter an http(s) image URL, a path starting with /, ./ or ../, or a data:image/ URL.
          </UrlError>
        )}
      </div>
    </Stack>
  );
}
